import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  PlusCircle,
  CalendarIcon,
  X,
  SlidersHorizontal,
  Download,
  Trash2,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  Tag,
  Check,
  XCircle,
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { Label } from '../ui/label';
import { AddDocumentDialog } from '../popups/AddDocumentDialog/AddDocumentDialog';
import { useDocumentsApi } from '../../hooks/useDocumentsApi';
import { type DocumentTypeCode, normalizeDocumentTypeCode } from '../../lib/utils';

export interface Document {
  id: string;
  name: string;
  status: 'processing' | 'ready' | 'error' | 'archived';
  tags: string[];
  mimeType?: string;
  validated?: boolean;
  supplier?: string;
  date: Date;
  documentType?: string;
  metadata?: Record<string, any>;
}

interface DocumentListProps {
  documents?: Document[];
  selectedDocument?: Document;
  onSelectDocument?: (doc: Document | undefined) => void;
  onDocumentArchived?: (docId: string) => void;
  onProcessingStarted?: () => void;
  onUploadCompleted?: (docId: string) => void;
  onError?: (message: string) => void;
  processingIds?: string[];
}

export const DocumentList: React.FC<DocumentListProps> = ({
  documents = [],
  selectedDocument,
  onSelectDocument,
  onDocumentArchived,
  onProcessingStarted,
  onUploadCompleted,
  onError,
  processingIds = [],
}) => {
  const api = useDocumentsApi();
  const { t } = useTranslation();
  const [isAddDocDialogOpen, setIsAddDocDialogOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    term: '',
    status: 'all',
    category: 'all',
    date: undefined as Date | undefined,
    supplier: '',
    valid: 'all' as 'all' | 'valid' | 'invalid',
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ready':
        return <CheckCircle2 className="h-4 w-4 text-accent" />;
      case 'processing':
        return <Clock className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-destructive" />;
      case 'archived':
        return <FileText className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getStatusText = (status: string) => t(`status.${status}`);

  const getTypeLabelFr = (type?: string) => t(`docType.${normalizeDocumentTypeCode(type)}`);

  const truncate = (value: string, max: number): string => {
    if (!value) return '';
    return value.length > max ? `${value.slice(0, max)}…` : value;
  };

  const isFiltered =
    filters.term !== '' ||
    filters.status !== 'all' ||
    filters.category !== 'all' ||
    filters.date !== undefined ||
    filters.valid !== 'all';

  const clearFilters = () => {
    setFilters({
      term: '',
      status: 'all',
      category: 'all',
      date: undefined,
      supplier: '',
      valid: 'all',
    });
  };

  const handleFilterChange = (key: keyof typeof filters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Always consider newest first ordering; assume parent provides date ordering but keep stable fallback
  const ordered = [...documents].sort((a, b) => {
    const at = a.date ? new Date(a.date).getTime() : 0;
    const bt = b.date ? new Date(b.date).getTime() : 0;
    return bt - at;
  });

  const filteredDocuments = ordered.filter((doc) => {
    // Handle 'archived' status separately
    if (filters.status === 'archived') {
      if (doc.status !== 'archived') return false;
    } else {
      // Hide archived documents from all other views
      if (doc.status === 'archived') return false;
      // Apply other status filters
      if (filters.status !== 'all' && doc.status !== filters.status) return false;
    }

    if (filters.term && !doc.name.toLowerCase().includes(filters.term.toLowerCase())) return false;
    if (filters.category !== 'all') {
      const selected = (filters.category as string).toUpperCase() as DocumentTypeCode | 'ALL';
      const docType = (doc.documentType || 'OTHER').toUpperCase() as DocumentTypeCode;
      const taxTypes: DocumentTypeCode[] = ['TAX_FORM', 'TPS_TVQ', 'T4_SLIPS', 'R1_SLIPS'];
      if (selected === 'TAX_FORM') {
        if (!taxTypes.includes(docType)) return false;
      } else if (docType !== selected) {
        return false;
      }
    }

    // Validated filter
    if (filters.valid === 'valid' && !doc.validated) return false;
    if (filters.valid === 'invalid' && !!doc.validated) return false;
    if (filters.date && new Date(doc.date).toDateString() !== filters.date.toDateString())
      return false;
    if (
      filters.supplier &&
      doc.supplier?.toLowerCase().includes(filters.supplier.toLowerCase()) === false
    )
      return false;
    return true;
  });

  // Ensure preview updates when filters change: select first visible document
  React.useEffect(() => {
    const isSelectedVisible = selectedDocument
      ? filteredDocuments.some((d) => d.id === selectedDocument.id)
      : false;

    // If list is empty OR selected doc is no longer visible, clear selection.
    if (
      (filteredDocuments.length === 0 && selectedDocument) ||
      (selectedDocument && !isSelectedVisible)
    ) {
      onSelectDocument?.(undefined);
    }
    // Do not auto-select any document. User must click to select.
  }, [filteredDocuments.length, selectedDocument?.id]);

  const handleDownload = async (e: React.MouseEvent, doc: Document) => {
    e.stopPropagation();
    try {
      await api.download(doc.id, doc.name);
    } catch (error) {
      console.error('Failed to download document:', error);
      setToast(t('toast.documents.downloadError'));
    }
  };

  const handleArchive = async (e: React.MouseEvent, docId: string) => {
    e.stopPropagation();
    try {
      await api.archive(docId);
      onDocumentArchived?.(docId);
    } catch (error) {
      console.error('Failed to archive document:', error);
      setToast(t('toast.documents.archiveError'));
    }
  };

  const handleRetry = async (e: React.MouseEvent, doc: Document) => {
    e.stopPropagation();
    try {
      await api.process(doc.id, doc.documentType || 'OTHER');
      // soft refresh: do not reload; user can refresh list from parent or next polling
      setToast(t('toast.documents.retryStart'));
      onProcessingStarted?.();
      onUploadCompleted?.(doc.id);
    } catch (err: any) {
      setToast(t('toast.documents.retryError'));
      onError?.(t('toast.documents.retryError'));
    }
  };

  return (
    <>
      <Card className="bg-card p-4 rounded-lg shadow-md flex flex-col h-full">
        {/* Section 1: Header */}
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">{t('documents') || 'Documents'}</h2>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => setIsAddDocDialogOpen(true)}
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            {t('actions.addDocument')}
          </Button>
        </div>

        {/* Section 2: Filters Container */}
        <div className="p-2 mt-4 border-y space-y-2">
          {/* Line 1: Search */}
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Recherche ..."
              className="pl-8 h-8 text-xs w-full"
              value={filters.term}
              onChange={(e) => handleFilterChange('term', e.target.value)}
            />
          </div>

          {/* Line 2: Core Filters */}
          <div className="flex items-center gap-1 mt-2 mb-3 min-w-0">
            <Select
              value={filters.status}
              onValueChange={(value) => handleFilterChange('status', value)}
            >
              <SelectTrigger className="h-8 text-xs flex-1 min-w-[90px]">
                <SelectValue placeholder={t('filters.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Statuts</SelectItem>
                <SelectItem value="ready">Prêt</SelectItem>
                <SelectItem value="processing">En cours</SelectItem>
                <SelectItem value="error">Erreur</SelectItem>
                <SelectItem value="archived">Archivé</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.category}
              onValueChange={(value) => handleFilterChange('category', value)}
            >
              <SelectTrigger className="h-8 text-xs flex-1 min-w-[100px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filters.categories')}</SelectItem>
                <SelectItem value="INVOICE">{t('docType.INVOICE')}</SelectItem>
                <SelectItem value="BANK_STATEMENT">{t('docType.BANK_STATEMENT')}</SelectItem>
                <SelectItem value="OTHER">{t('docType.OTHER')}</SelectItem>
                <SelectGroup>
                  <SelectLabel>{t('docType.TAX_FORM')}</SelectLabel>
                  <SelectItem value="TAX_FORM">{t('docType.TAX_FORM')}</SelectItem>
                  <SelectItem value="TPS_TVQ">{t('docType.TPS_TVQ')}</SelectItem>
                  <SelectItem value="T4_SLIPS">{t('docType.T4_SLIPS')}</SelectItem>
                  <SelectItem value="R1_SLIPS">{t('docType.R1_SLIPS')}</SelectItem>
                </SelectGroup>
                <SelectItem value="LAW_TEXT_OR_PRESS_RELEASE">
                  {t('docType.LAW_TEXT_OR_PRESS_RELEASE')}
                </SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="h-8 font-normal justify-start text-left text-xs flex-1 min-w-0 truncate"
                >
                  <CalendarIcon className="h-4 w-4" />
                  {filters.date ? filters.date.toLocaleDateString() : t('filters.date')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={filters.date}
                  onSelect={(date) => handleFilterChange('date', date || undefined)}
                />
              </PopoverContent>
            </Popover>

            {isFiltered && (
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={clearFilters}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Line 3: Advanced Filters */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-8 text-xs w-full justify-start text-muted-foreground"
              >
                <SlidersHorizontal className="h-4 w-4 mr-2" />
                {t('filters.title')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-4 space-y-3">
              {filters.category === 'INVOICE' && (
                <div className="space-y-2">
                  <Label htmlFor="supplier" className="text-xs">
                    {t('filters.supplier')}
                  </Label>
                  <Input
                    id="supplier"
                    className="h-8 text-xs"
                    value={filters.supplier}
                    onChange={(e) => handleFilterChange('supplier', e.target.value)}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-xs">{t('filters.validity')}</Label>
                <Select
                  value={filters.valid}
                  onValueChange={(value) => handleFilterChange('valid', value)}
                >
                  <SelectTrigger className="h-8 text-xs w-full">
                    <SelectValue placeholder={t('filters.validity')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('filters.all')}</SelectItem>
                    <SelectItem value="valid">{t('filters.valid')}</SelectItem>
                    <SelectItem value="invalid">{t('filters.invalid')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Liste des documents */}
        <ScrollArea className="flex-1 mt-2 w-full">
          <ul className="space-y-2 p-1 w-full">
            {filteredDocuments.map((doc) => (
              <li
                key={doc.id}
                className={`
                relative flex items-center p-2 rounded-md cursor-pointer group overflow-hidden w-full
                ${selectedDocument?.id === doc.id ? 'bg-primary/10 ring-2 ring-primary' : 'hover:bg-secondary/80'}
              `}
                onClick={() => onSelectDocument?.(doc)}
              >
                <div className="flex-1 min-w-0 w-0">
                  <p
                    className="text-sm font-medium truncate max-w-full break-words"
                    title={doc.name}
                  >
                    {doc.name.length > 20 ? `${doc.name.slice(0, 20)}…` : doc.name}
                  </p>
                  <div className="grid grid-cols-[auto,minmax(0,1fr),auto] items-center mt-1 min-w-0 w-full gap-2">
                    {/* Left: Status */}
                    <div className="flex items-center gap-1 min-w-0">
                      {getStatusIcon(doc.status)}
                      <span className="text-[11px] text-muted-foreground truncate">
                        {getStatusText(doc.status)}
                      </span>
                    </div>

                    {/* Middle: Document Type */}
                    <div className="flex items-center justify-center min-w-0">
                      {doc.documentType && (
                        <Badge
                          variant="secondary"
                          className="h-5 px-2 py-0 text-[11px] bg-gray-100 text-gray-700 border border-gray-200 max-w-[95%] truncate"
                          title={getTypeLabelFr(doc.documentType)}
                        >
                          <Tag className="h-3 w-3 mr-1 shrink-0" />
                          <span className="truncate">
                            {truncate(getTypeLabelFr(doc.documentType), 15)}
                          </span>
                        </Badge>
                      )}
                    </div>

                    {/* Right: Validated Status */}
                    <div className="flex items-center justify-end gap-1 min-w-0">
                      {doc.validated ? (
                        <Badge className="h-5 px-2 py-0 text-[11px] bg-green-100 text-green-800 border-green-200">
                          <Check className="h-3 w-3 mr-1" />
                          {t('filters.valid')}
                        </Badge>
                      ) : (
                        <Badge
                          variant="destructive"
                          className="h-5 px-2 py-0 text-[11px] bg-red-100 text-red-800 border-red-200"
                        >
                          <XCircle className="h-3 w-3 mr-1" />
                          {t('filters.invalid')}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions - overlay top-right, visible on hover */}
                <div className="absolute top-2 right-2 hidden group-hover:flex items-center gap-1 pointer-events-auto">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={(e) => handleDownload(e, doc)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  {doc.status !== 'archived' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-600 hover:text-red-600 hover:bg-red-100"
                      onClick={(e) => handleArchive(e, doc.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                  {(doc.status === 'error' ||
                    (doc.status === 'processing' && processingIds.includes(doc.id))) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-blue-600 hover:text-blue-600 hover:bg-blue-100"
                      onClick={(e) => handleRetry(e, doc)}
                      title="Relancer le traitement"
                    >
                      {/* Retry icon (↻) */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="h-4 w-4"
                      >
                        <path d="M12 5v2a5 5 0 1 1-4.546 2.916.75.75 0 1 0-1.341-.668A6.5 6.5 0 1 0 12 4.25V3a.75.75 0 0 0-1.28-.53l-2.5 2.5a.75.75 0 0 0 0 1.06l2.5 2.5A.75.75 0 0 0 12 8V5z" />
                      </svg>
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </ScrollArea>
      </Card>
      {toast && (
        <div
          className="fixed bottom-4 right-4 bg-black text-white text-sm px-3 py-2 rounded shadow"
          onClick={() => setToast(null)}
        >
          {toast}
        </div>
      )}
      <AddDocumentDialog
        isOpen={isAddDocDialogOpen}
        onClose={() => setIsAddDocDialogOpen(false)}
        onCompleted={(doc) => {
          onUploadCompleted?.(doc._id);
        }}
        onProcessingStarted={() => {
          onProcessingStarted?.();
        }}
        onError={(m) => {
          setToast(m);
          onError?.(m);
        }}
      />
    </>
  );
};
